import { StudiestedForAvtale } from '../index';
import {
  BrukerrollerDto,
  HttpError,
  MinimalStudiestedDto,
  MuligeStudiestedDto,
  StudiestedbrukerDto,
  StudiestedbrukerSignatarDto,
} from '../../../api/generated/studiested-generated-api';
import React, { useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import studiestedTranslations from './studiestedsection.i18n';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../../components/table/alpTable';
import { NavLink } from 'react-router-dom';
import { StudiestedApiContext } from '../../../api/StudiestedApiContext';
import { useLoader } from '../../../api/useLoader';
import Autocomplete from '../../../components/input/autocomplete';
import {
  getMuligStudiestedDybde,
  IndentMuligStudiested,
} from '../../../components/indentation/indentation';
import Spinner from '../../../components/spinner/spinner';
import { ModalTable, useSelectedStedContext } from './index';
import PanelHeader from '../../../components/panelheader/panelHeader';
import * as css from './stedsection.module.scss';
import { FakeLockedInput } from '../../../components/input';
import { NotificationFunctionContext } from '../../../components/notifications/notificationProvider';
import { devEnvFlag, testEnvFlag } from '../../../util/featureFlags';

interface StudiestedTableProps {
  sted: StudiestedForAvtale;
}

enum StudiestedTableHeader {
  INSTITUSJON = 'INSTITUSJON',
  AVDELING = 'AVDELING',
  SIGNATAR = 'SIGNATAR',
}

export const getInstitutionName = (sted: MinimalStudiestedDto): string =>
  sted.noder.over.reduce(
    (_, node: MinimalStudiestedDto) => getInstitutionName(node),
    sted.navn,
  );

export const StudiestedTable: React.FC<
  React.PropsWithChildren<StudiestedTableProps>
> = ({ sted }) => {
  const { formatMessage } = useIntl();
  const tableHeaders = useMemo(
    () =>
      devEnvFlag || testEnvFlag
        ? {
            [StudiestedTableHeader.INSTITUSJON]: formatMessage({
              id: studiestedTranslations.institusjonHeader.id,
            }),
            [StudiestedTableHeader.AVDELING]: formatMessage({
              id: studiestedTranslations.avdelingHeader.id,
            }),
            [StudiestedTableHeader.SIGNATAR]: formatMessage({
              id: studiestedTranslations.signatarHeader.id,
            }),
          }
        : {
            [StudiestedTableHeader.INSTITUSJON]: formatMessage({
              id: studiestedTranslations.institusjonHeader.id,
            }),
            [StudiestedTableHeader.AVDELING]: formatMessage({
              id: studiestedTranslations.avdelingHeader.id,
            }),
          },
    [formatMessage],
  );

  const headers = useMemo(
    () =>
      Object.entries(tableHeaders).map(([type, title]) => (
        <TableCellTh key={`${title}-${type}`}>{title}</TableCellTh>
      )),
    [tableHeaders],
  );

  const rows = useMemo(() => {
    const { signatar, navn } = sted;
    const signatarNavn = signatar
      ? `${signatar.fornavn} ${signatar.etternavn}`
      : '';

    return (
      <TableRow>
        <TableCellTd header={tableHeaders[StudiestedTableHeader.INSTITUSJON]}>
          {getInstitutionName(sted)}
        </TableCellTd>
        <TableCellTd header={tableHeaders[StudiestedTableHeader.AVDELING]}>
          {navn}
        </TableCellTd>
        {(devEnvFlag || testEnvFlag) && (
          <TableCellTd header={tableHeaders[StudiestedTableHeader.SIGNATAR]!!}>
            {signatar && (
              <>
                {signatar?.studiestedbrukerId ? (
                  <NavLink
                    to={`/myinstitution/studiestedbrukerprofil/${signatar.studiestedbrukerId}`}
                  >
                    {signatarNavn}
                  </NavLink>
                ) : (
                  signatarNavn
                )}
              </>
            )}
          </TableCellTd>
        )}
      </TableRow>
    );
  }, [tableHeaders, sted]);

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

export const StudiestedViewMode: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { toppnode } = useSelectedStedContext();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalStedFieldView: formatMessage({
        id: studiestedTranslations.modalStedFieldView.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <>
      <FakeLockedInput
        id={'institution-select'}
        label={labels.modalStedFieldView}
        value={toppnode?.navn}
      />
      {toppnode && <ModalTable />}
    </>
  );
};

export const StudiestedSelect: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { studiestedApi } = useContext(StudiestedApiContext);
  const { addErrorNotification } = useContext(NotificationFunctionContext);
  const { toppnode, setToppnode } = useSelectedStedContext();

  const { data: muligeStudiesteder, loading: loadingMuligeSteder } = useLoader(
    () => studiestedApi.muligeStudiestederGet(),
    [],
  );

  const [loadingSted, setLoadingSted] = useState(false);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalStedField: formatMessage({
        id: studiestedTranslations.modalStedField.id,
      }),
      modalStedFieldPlaceholder: formatMessage({
        id: studiestedTranslations.modalStedFieldPlaceholder.id,
      }),
    }),
    [formatMessage],
  );

  const onSelect = (_: object, value: MuligeStudiestedDto | undefined) => {
    if (value?.id) {
      setLoadingSted(true);

      studiestedApi
        .studiestedMedHierarkiIdGet({
          pathParams: { id: value?.id },
        })
        .then((sted) => {
          setToppnode(sted);
        })
        .catch((err: HttpError) => addErrorNotification(err.message))
        .finally(() => setLoadingSted(false));
    } else {
      setToppnode(undefined);
    }
  };

  if (loadingMuligeSteder || !muligeStudiesteder) return <Spinner />;

  return (
    <>
      <Autocomplete
        id={'institution-select'}
        label={labels.modalStedField}
        options={muligeStudiesteder.map((muligSted) => ({
          ...muligSted,
          dybde: getMuligStudiestedDybde(muligSted),
        }))}
        onChange={onSelect}
        renderOption={(option) => <IndentMuligStudiested studiested={option} />}
        selectedOption={muligeStudiesteder.find(
          (muligSted) => muligSted.id === toppnode?.id,
        )}
        placeholder={labels.modalStedFieldPlaceholder}
      />
      {loadingSted && <Spinner />}
      {toppnode && !loadingSted && (
        <>
          <ModalTable />
          {children}
        </>
      )}
    </>
  );
};

interface StudiestedSigneringSectionProps {
  setSignatar: (
    studiestedbruker: StudiestedbrukerSignatarDto | undefined,
  ) => void;
  signatar?: StudiestedbrukerDto;
  toppnode: MinimalStudiestedDto;
}

export const StudiestedSigneringSection: React.FC<
  React.PropsWithChildren<StudiestedSigneringSectionProps>
> = ({ setSignatar, signatar, toppnode }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      signeringTitle: formatMessage({
        id: studiestedTranslations.signeringTitle.id,
      }),
      signeringSubtitle: formatMessage({
        id: studiestedTranslations.signeringSubtitle.id,
      }),
      ansvarligLabel: formatMessage({
        id: studiestedTranslations.ansvarligLabel.id,
      }),
      epostLabel: formatMessage({ id: studiestedTranslations.epostLabel.id }),
      tlfLabel: formatMessage({ id: studiestedTranslations.tlfLabel.id }),
      ansvarligPlaceholder: formatMessage({
        id: studiestedTranslations.ansvarligPlaceholder.id,
      }),
      lockedInputPlaceholder: formatMessage({
        id: studiestedTranslations.lockedInputPlaceholder.id,
      }),
    }),
    [formatMessage],
  );

  const { studiestedApi } = useContext(StudiestedApiContext);
  const { data: studiestedbrukere } = useLoader(
    () =>
      studiestedApi.studiestedbrukereGet({
        queryParams: { studiestedId: toppnode.id },
      }),
    [toppnode],
  );

  // Tests on highest access level - if it needs to be specific to a chosen place, change later
  const studiestedbrukereWithRegisterAccess = useMemo(
    () =>
      studiestedbrukere?.filter((bruker) =>
        [
          BrukerrollerDto.Administrator as BrukerrollerDto,
          BrukerrollerDto.Registrererettighet as BrukerrollerDto,
        ].includes(bruker.tilgangsrolle),
      ),
    [studiestedbrukere],
  );

  return (
    <>
      <PanelHeader
        title={labels.signeringTitle}
        subtitle={labels.signeringSubtitle}
      />
      <div className={css.inputFieldRow}>
        <div className={css.textInput}>
          <Autocomplete
            id={'ansvarlig-ansatt'}
            label={labels.ansvarligLabel}
            onChange={(_, value: StudiestedbrukerDto | undefined) =>
              setSignatar(
                value
                  ? mapStudiestedbrukerToStudiestedbrukerSignatarDto(value)
                  : undefined,
              )
            }
            placeholder={labels.ansvarligPlaceholder}
            options={studiestedbrukereWithRegisterAccess?.map((bruker) => ({
              ...bruker,
              navn: `${bruker.fornavn} ${bruker.etternavn}`,
            }))}
            selectedOption={
              signatar
                ? {
                    ...signatar,
                    navn: `${signatar.fornavn} ${signatar.etternavn}`,
                  }
                : undefined
            }
          />
        </div>
        <FakeLockedInput
          containerClassName={css.textInput}
          label={labels.epostLabel}
          value={signatar ? signatar.epost : labels.lockedInputPlaceholder}
        />
      </div>
      <div className={css.inputFieldRow}>
        <FakeLockedInput
          containerClassName={css.textInput}
          label={labels.tlfLabel}
          value={
            signatar ? signatar.telefonnummer : labels.lockedInputPlaceholder
          }
        />
      </div>
    </>
  );
};

const mapStudiestedbrukerToStudiestedbrukerSignatarDto = (
  studiestedbruker: StudiestedbrukerDto,
): StudiestedbrukerSignatarDto => ({
  ...studiestedbruker,
  studiestedId: studiestedbruker.tilhorighet?.studiested.id,
  studiestedbrukerId: studiestedbruker.id,
});
